import type {ChangeSpec} from '@codemirror/state'
import type {Delimiter, ListItem} from 'mdast'
import {EXIT, SKIP, visit} from 'unist-util-visit'

import {createText} from '../../codemirror/extensions/single-line-editor'
import {buildContinuationSyntax} from './continuation-utils'

const defaultUnorderedListMarker = '* '
const defaultTaskListMarker = '- '

interface OrderedListMarker {
  prefix: string
  digits: number
  suffix: string
}

const OrderedListMarker = {
  first: {prefix: '', digits: 1, suffix: '. '},
  /** @see https://github.github.com/gfm/#ordered-list-marker */
  parse(raw: string) {
    // Theoretically there wouldn't ever be a whitespace prefix since that would be a listItemIndent token, but it
    // doesn't hurt to allow it just in case
    const match = raw.match(/^(?<prefix>\s*)(?<digits>\d{1,9})(?<suffix>[.)]\s*)$/)?.groups as
      | {
          prefix: string
          digits: string
          suffix: string
        }
      | undefined
    return match ? {...match, digits: parseInt(match.digits, 10)} : undefined
  },
  serialize(marker: OrderedListMarker) {
    return `${marker.prefix}${marker.digits}${marker.suffix}`
  },
  increment(marker: OrderedListMarker) {
    // The spec states that markers cannot have more than nine digits
    return {...marker, digits: Math.min(marker.digits + 1, 999_999_999)}
  },
  reset(marker: OrderedListMarker) {
    return {...marker, digits: 1}
  },
}

interface TasklistPrefix {
  prefix: string
  value: string
  suffix: string
}

const TasklistPrefix = {
  checked: {prefix: '[', value: 'x', suffix: '] '},
  unchecked: {prefix: '[', value: ' ', suffix: '] '},
  /** @see https://github.github.com/gfm/#task-list-item-marker */
  parse(raw: string) {
    return raw.match(/^(?<prefix>\[)(?<value>[ xX])(?<suffix>\]\s*)$/)?.groups as TasklistPrefix | undefined
  },
  serialize(marker: TasklistPrefix) {
    return `${marker.prefix}${marker.value}${marker.suffix}`
  },
}

/** Get the next list item marker string from this item. For unordered items, returns the same marker. */
export function buildNextMarkerSyntax(marker: string) {
  const ordered = OrderedListMarker.parse(marker)
  return ordered ? OrderedListMarker.serialize(OrderedListMarker.increment(ordered)) : marker
}

/** Get the marker string for the first item matching this list. */
export function buildResetMarkerSyntax(marker: string) {
  const ordered = OrderedListMarker.parse(marker)
  return ordered ? OrderedListMarker.serialize(OrderedListMarker.reset(ordered)) : marker
}

/** Get the marker string for the first item in a new list. */
export function buildFirstMarkerSyntax(ordered: boolean) {
  return ordered ? OrderedListMarker.serialize(OrderedListMarker.first) : defaultUnorderedListMarker
}

/** Create tasklist prefix syntax. */
export function buildTasklistPrefixSyntax(checked: boolean) {
  return TasklistPrefix.serialize(checked ? TasklistPrefix.checked : TasklistPrefix.unchecked)
}

/** Create a full tasklist marker (list marker + checkbox syntax). */
export function buildTasklistMarkerSyntax(checked: boolean) {
  return `${defaultTaskListMarker}${buildTasklistPrefixSyntax(checked)}`
}

export function isTasklistPrefix(prefix: string) {
  return TasklistPrefix.parse(prefix) !== undefined
}

export function isChecked(prefix: string) {
  return TasklistPrefix.parse(prefix)?.value.toLowerCase() === 'x'
}

/**
 * Determine nesting depth by counting list-related prefixes before the final list marker.
 */
function getListNestingDepth(prefixes: readonly Delimiter[], listPrefixIndex: number): number {
  if (listPrefixIndex < 0) return 0
  const linePrefix = prefixes.find(p => p.tokenType === 'linePrefix')
  if (linePrefix) {
    // Every 2 spaces in line prefix is one level of nesting
    return Math.floor(linePrefix.raw.length / 2)
  }

  return prefixes
    .slice(0, listPrefixIndex)
    .filter(p => p.tokenType === 'listItemPrefix' || p.tokenType === 'listItemIndent').length
}

/**
 * Find the task list checkbox delimiter in the given prefix list, if present.
 */
function findTaskCheckbox(prefixes: readonly Delimiter[], listPrefixIndex: number): Delimiter | undefined {
  return prefixes.slice(listPrefixIndex).find(p => p.tokenType === 'taskListCheck')
}

/**
 * Build prefix syntax for pasting list items into an existing list.
 * Handles three scenarios based on relative nesting depth:
 * 1. Same depth: Use context marker, preserve task checkbox state from inserted content
 * 2. More or less nested: Preserve inserted structure
 */
function buildListPrefixSyntax(prefixes: readonly Delimiter[], insertedPrefixes: readonly Delimiter[]): string {
  const contextListPrefixIndex = prefixes.findLastIndex(prefix => prefix.tokenType === 'listItemPrefix')
  const insertedListPrefixIndex = insertedPrefixes.findLastIndex(prefix => prefix.tokenType === 'listItemPrefix')

  const contextTaskCheck = findTaskCheckbox(prefixes, contextListPrefixIndex)
  const insertedTaskCheck = findTaskCheckbox(insertedPrefixes, insertedListPrefixIndex)

  const contextNestingDepth = getListNestingDepth(prefixes, contextListPrefixIndex)
  const insertedNestingDepth = getListNestingDepth(insertedPrefixes, insertedListPrefixIndex)

  // Case 1: Same nesting depth - use context marker but preserve task checkbox state
  if (insertedNestingDepth === contextNestingDepth) {
    const continuation = buildContinuationSyntax(prefixes.slice(0, contextListPrefixIndex))
    const listBase = continuation + prefixes[contextListPrefixIndex]?.raw
    if (!contextTaskCheck) return listBase
    return listBase + (insertedTaskCheck?.raw ?? buildTasklistPrefixSyntax(false))
  }

  // Case 2: Different nesting depth - preserve indentation from inserted content
  return insertedPrefixes.map(p => p.raw).join('')
}

/**
 * Handles pasting list items into an existing list context.
 * Adjusts list markers and indentation to match the destination.
 *
 * @param contextStart - Document offset where the paste begins
 * @param contextPrefixes - Delimiters from the destination list item
 * @param insertedMarkdownLine - Parsed line from the pasted content
 * @returns Change specifications, or empty array if no changes needed
 */
export function handleListItemPaste(
  contextStart: number,
  contextPrefixes: Delimiter[],
  insertedPrefixes: Delimiter[],
): ChangeSpec[] {
  // Check if this inserted line has a list marker
  const hasListMarker = insertedPrefixes.findLast(prefix => prefix.tokenType === 'listItemPrefix')
  const startPrefixOffset = insertedPrefixes[0]?.position?.start.offset
  const endPrefixOffset = insertedPrefixes.at(-1)?.position?.end.offset

  // Fallback to default
  if (!hasListMarker || startPrefixOffset === undefined || endPrefixOffset === undefined) return []

  const newSyntax = buildListPrefixSyntax(contextPrefixes, insertedPrefixes)
  if (!newSyntax) return []

  // Replace the line's prefix with new syntax
  return [
    {
      from: contextStart + startPrefixOffset,
      to: contextStart + endPrefixOffset,
      insert: createText(newSyntax),
    },
  ]
}

/** Search for the list item marker and tasklist prefix in this item. */
export function findItemPrefixes(item: ListItem): {listItemPrefix?: Delimiter; taskListPrefix?: Delimiter} {
  const result: {listItemPrefix?: Delimiter; taskListPrefix?: Delimiter} = {}

  // We can't just look in `item.children` because the prefix will likely be inside a paragraph or potentially deeper
  visit(item, node => {
    if (result.listItemPrefix) {
      if (node.type === 'delimiter' && node.tokenType === 'taskListCheck') result.taskListPrefix = node

      return EXIT
    }

    if (node.type === 'delimiter' && node.tokenType === 'listItemPrefix') result.listItemPrefix = node

    // This shouldn't happen because we should always find a prefix before we ever find a nested list, but just
    // in case the tree is invalid we wouldn't want to accidentally return a prefix from a sub-item.
    if (node.type === 'list') return SKIP
  })

  return result
}
